import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { Card, CardHeader, CardContent, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { 
    ArrowLeft, Check, ChevronLeft, ChevronRight, AlertCircle, 
    Stethoscope, X, Info, CheckCircle2, Calendar, FileText, 
    TrendingUp, ShieldAlert, Sparkles, User, ArrowRight
} from 'lucide-react';
import { SCREENING_SECTIONS } from '../../lib/screeningQuestions';
import { evaluateScreeningAnswers } from '../../lib/screeningEvaluator';
import ScreeningAssessment from '../../components/assessments/ScreeningAssessment';

export default function PatientAssessmentFlow() {
    const { user } = useAuth();
    const { submitAssessment, patientQuestionnaires } = useData();
    const navigate = useNavigate();
    const location = useLocation();

    const requiredType = location.state?.requiredType;
    
    // Fuzzy matching for screening tools
    const normalizedRequiredType = requiredType?.toLowerCase().trim();
    const isScreening = !requiredType || normalizedRequiredType?.includes('child developmental screening') || normalizedRequiredType?.includes('screening');
    
    // Fallback to first questionnaire if not specified
    const matchedForm = patientQuestionnaires.find(q => q.title === (isScreening ? 'Child Developmental Screening' : requiredType)) || patientQuestionnaires[0];
    const questions = matchedForm?.questions || [];

    const DOMAINS = isScreening ? SCREENING_SECTIONS : questions;
    const assessmentTitle = isScreening ? 'Child Developmental Screening' : matchedForm?.title || 'Unknown Assessment';
    const draftKey = `p2g_draft_patient_${user?.patientId || user?.id || 'guest'}_${assessmentTitle.replace(/\s+/g, '_')}`;

    // Workflow Stages: 0 to DOMAINS.length - 1 (Questions), DOMAINS.length (Review Step), DOMAINS.length + 1 (Outcome Step)
    const REVIEW_STEP = DOMAINS.length;
    const OUTCOME_STEP = DOMAINS.length + 1;

    const [currentStep, setCurrentStep] = useState(() => {
        const saved = localStorage.getItem(draftKey);
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                return parsed.currentStep !== undefined ? Math.min(parsed.currentStep, DOMAINS.length) : 0;
            } catch (e) { return 0; }
        }
        return 0;
    });

    const [answers, setAnswers] = useState(() => {
        const saved = localStorage.getItem(draftKey);
        if (saved) {
            try {
                return JSON.parse(saved).answers || {};
            } catch (e) { return {}; }
        }
        return {};
    });

    const [remarks, setRemarks] = useState(() => {
        const saved = localStorage.getItem(draftKey);
        if (saved) {
            try {
                return JSON.parse(saved).remarks || {};
            } catch (e) { return {}; }
        }
        return {};
    });

    const [additionalNotes, setAdditionalNotes] = useState('');
    const [submissionResult, setSubmissionResult] = useState(null);
    const [restored, setRestored] = useState(false);

    // Initial restore notification
    useEffect(() => {
        if (Object.keys(answers).length > 0 || currentStep > 0) {
            setRestored(true);
            const timer = setTimeout(() => setRestored(false), 3000);
            return () => clearTimeout(timer);
        }
    }, []);

    // Autosave effect (skip if on final outcome step)
    useEffect(() => {
        if (currentStep < OUTCOME_STEP && (Object.keys(answers).length > 0 || Object.keys(remarks).length > 0 || currentStep > 0)) {
            const draft = {
                answers,
                remarks,
                currentStep,
                lastSaved: new Date().toISOString()
            };
            localStorage.setItem(draftKey, JSON.stringify(draft));
        }
    }, [answers, remarks, currentStep, draftKey]);

    // Safety check for empty domains
    if (!DOMAINS || DOMAINS.length === 0) {
        return (
            <div className="max-w-xl mx-auto py-20 px-6 text-center">
                <div className="bg-white p-12 rounded-[2.5rem] shadow-premium border border-slate-100">
                    <div className="h-20 w-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-8">
                        <AlertCircle className="h-10 w-10 text-red-500" />
                    </div>
                    <h2 className="text-2xl font-black text-slate-900 font-outfit mb-4 uppercase tracking-tight">Assessment Protocol Missing</h2>
                    <p className="text-slate-500 mb-8 leading-relaxed font-medium">We couldn't find the questionnaire for <b>"{requiredType || 'Unspecified'}"</b>. Please contact your clinician to re-assign the module.</p>
                    <Button onClick={() => navigate('/Screening_test/patient')} className="h-12 rounded-xl px-8 bg-slate-900 text-white font-bold text-xs uppercase tracking-widest shadow-md">
                        Go Back To Dashboard
                    </Button>
                </div>
            </div>
        );
    }

    const handleAnswer = (questionId, value) => {
        setAnswers(prev => ({ ...prev, [questionId]: value }));
    };

    const handleSelect = (value) => {
        setAnswers(prev => ({ ...prev, [currentStep]: value }));
    };

    const handleRemarkChange = (e) => {
        setRemarks(prev => ({ ...prev, [currentStep]: e.target.value }));
    };

    const handleCheckboxToggle = (opt) => {
        const existing = Array.isArray(answers[currentStep]) ? answers[currentStep] : [];
        const next = existing.includes(opt) ? existing.filter(x => x !== opt) : [...existing, opt];
        handleSelect(next);
    };

    // Validation for moving forward
    const isStepComplete = () => {
        if (currentStep === REVIEW_STEP) return true;

        if (isScreening) {
            const section = SCREENING_SECTIONS[currentStep];
            if (!section) return false;
            // Quantitative options must be answered; free text is optional
            return section.questions.every(q => {
                if (q.type === 'textarea' || q.type === 'text') return true;
                return answers[q.id] !== undefined && answers[q.id] !== '';
            });
        }

        const currentQ = questions[currentStep];
        if (typeof currentQ === 'object' && currentQ !== null) {
            if (currentQ.required === false) return true;
            const ans = answers[currentStep];
            if (ans === undefined || ans === null || ans === '') return false;
            if (Array.isArray(ans) && ans.length === 0) return false;
            if (typeof ans === 'string' && ans.trim().length === 0) return false;
            return true;
        }

        return answers[currentStep] !== undefined;
    };

    const handleNext = () => {
        if (currentStep < DOMAINS.length - 1) {
            setCurrentStep(curr => curr + 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } else if (currentStep === DOMAINS.length - 1) {
            // Move to review & confirm step
            setCurrentStep(REVIEW_STEP);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handleFinalSubmit = () => {
        let calculatedScore = 0;
        let calculatedMaxScore = 0;
        let calculatedPercentage = 0;
        let evaluationData = null;

        if (isScreening) {
            const mergedRemarks = { ...remarks, generalNotes: additionalNotes };
            evaluationData = evaluateScreeningAnswers(answers, mergedRemarks);
            calculatedScore = evaluationData.score;
            calculatedMaxScore = evaluationData.maxScore;
            calculatedPercentage = evaluationData.percentage;
        } else {
            let totalEarned = 0;
            let totalPossible = 0;

            questions.forEach((q, idx) => {
                const ans = answers[idx];
                if (typeof q === 'object' && q !== null) {
                    if (q.type === 'rating') {
                        const max = q.scaleMax || 5;
                        totalPossible += max;
                        if (typeof ans === 'number') totalEarned += ans;
                    } else if (q.type === 'boolean') {
                        totalPossible += 1;
                        if (ans === 'Yes') totalEarned += 1;
                    } else if (q.type === 'multiple_choice' || q.type === 'dropdown') {
                        totalPossible += 1;
                        if (ans !== undefined && ans !== '') totalEarned += 1;
                    } else if (q.type === 'checkbox') {
                        totalPossible += 1;
                        if (Array.isArray(ans) && ans.length > 0) totalEarned += 1;
                    } else {
                        totalPossible += 1;
                        if (ans && typeof ans === 'string' && ans.trim().length > 0) totalEarned += 1;
                    }
                } else {
                    totalPossible += 4;
                    if (typeof ans === 'number') totalEarned += ans;
                }
            });

            calculatedMaxScore = totalPossible || questions.length * 4;
            calculatedScore = totalEarned;
            calculatedPercentage = calculatedMaxScore > 0 ? Math.round((calculatedScore / calculatedMaxScore) * 100) : 0;
        }

        const submissionPayload = {
            patientId: user?.patientId || user?.id || 1,
            patientName: user?.patientName || user?.name || 'Guest',
            type: isScreening ? 'Child Developmental Screening' : (matchedForm?.title || 'General Assessment'),
            submitterRole: 'Patient',
            answers,
            remarks: { ...remarks, additionalNotes },
            score: calculatedScore,
            maxScore: calculatedMaxScore,
            percentage: calculatedPercentage,
            riskLevel: evaluationData?.riskLevel || (calculatedPercentage >= 75 ? 'Low' : calculatedPercentage >= 55 ? 'Moderate' : 'Elevated'),
            riskDescription: evaluationData?.riskDescription || '',
            domainBreakdown: evaluationData?.domainBreakdown || [],
            redFlags: evaluationData?.redFlags || [],
            recommendedSpecialists: evaluationData?.recommendedSpecialists || [],
            date: new Date().toISOString()
        };

        // Submit to data provider
        submitAssessment(submissionPayload);
        
        // Save to local screening cache for doctor appointment integration
        try {
            localStorage.setItem('p2g_latest_screening', JSON.stringify(submissionPayload));
        } catch (e) {}

        // Clear local draft
        localStorage.removeItem(draftKey);

        // Store result for the outcome step
        setSubmissionResult(submissionPayload);
        setCurrentStep(OUTCOME_STEP);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handlePrev = () => {
        if (currentStep > 0) {
            setCurrentStep(s => s - 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    // Calculate overall completion percentage for stepper
    const progress = Math.min(100, Math.round(((currentStep + 1) / (DOMAINS.length + 1)) * 100));

    // Custom questionnaire question metadata
    const currentQ = !isScreening && currentStep < DOMAINS.length ? questions[currentStep] : null;
    const isObjectQ = typeof currentQ === 'object' && currentQ !== null;
    const questionTitle = isObjectQ ? (currentQ.title || `Item #${currentStep + 1}`) : (typeof currentQ === 'string' ? currentQ : `Item #${currentStep + 1}`);
    const questionDescription = isObjectQ ? currentQ.description : '';
    const questionType = isObjectQ ? (currentQ.type || 'multiple_choice') : 'legacy_rating';
    const isRequired = isObjectQ ? (currentQ.required !== false) : true;
    const currentAnswer = answers[currentStep];

    // ==========================================
    // STEP 4: POST-SCREENING OUTCOME VIEW
    // ==========================================
    if (currentStep === OUTCOME_STEP && submissionResult) {
        const isLowRisk = submissionResult.riskLevel === 'Low';
        const isElevated = submissionResult.riskLevel === 'Elevated';

        return (
            <div className="max-w-3xl mx-auto space-y-8 py-10 px-4 animate-in fade-in duration-500">
                {/* Success Card */}
                <div className="bg-white rounded-[2.5rem] border border-slate-200/80 shadow-premium p-8 sm:p-12 text-center space-y-6 relative overflow-hidden">
                    <div className="absolute top-0 inset-x-0 h-2 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500" />
                    
                    <div className="h-20 w-20 rounded-3xl bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto shadow-sm ring-8 ring-emerald-50/50">
                        <CheckCircle2 className="h-10 w-10" />
                    </div>

                    <div className="space-y-2">
                        <span className="text-[11px] font-black uppercase tracking-widest text-emerald-600 bg-emerald-50 border border-emerald-200/60 px-3 py-1 rounded-full font-outfit">
                            Assessment Successfully Recorded
                        </span>
                        <h1 className="text-2xl sm:text-4xl font-black text-slate-900 font-outfit tracking-tight">
                            Screening Completed!
                        </h1>
                        <p className="text-slate-500 text-sm max-w-lg mx-auto font-medium">
                            Responses for <b>{submissionResult.patientName}</b> have been evaluated against standardized clinical milestones.
                        </p>
                    </div>

                    {/* Score Summary Box */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4">
                        <div className="bg-slate-50 p-5 rounded-2xl border border-slate-100">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Milestone Score</p>
                            <p className="text-3xl font-black text-indigo-600 font-outfit mt-1">
                                {submissionResult.percentage}%
                            </p>
                            <p className="text-xs text-slate-500 font-medium mt-0.5">
                                {submissionResult.score} of {submissionResult.maxScore} points
                            </p>
                        </div>

                        <div className="bg-slate-50 p-5 rounded-2xl border border-slate-100">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Clinical Risk Tier</p>
                            <p className={`text-2xl font-black font-outfit mt-1.5 ${
                                isLowRisk ? 'text-emerald-600' : isElevated ? 'text-red-600' : 'text-amber-600'
                            }`}>
                                {submissionResult.riskLevel} Concern
                            </p>
                            <p className="text-xs text-slate-500 font-medium mt-0.5">Developmental Profile</p>
                        </div>

                        <div className="bg-slate-50 p-5 rounded-2xl border border-slate-100">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Flags Detected</p>
                            <p className={`text-3xl font-black font-outfit mt-1 ${
                                submissionResult.redFlags?.length > 0 ? 'text-amber-600' : 'text-emerald-600'
                            }`}>
                                {submissionResult.redFlags?.length || 0}
                            </p>
                            <p className="text-xs text-slate-500 font-medium mt-0.5">Priority Markers</p>
                        </div>
                    </div>

                    {/* Domain Breakdown Snapshot */}
                    {submissionResult.domainBreakdown && submissionResult.domainBreakdown.length > 0 && (
                        <div className="pt-4 text-left space-y-3">
                            <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest">
                                Domain Progress Snapshot
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {submissionResult.domainBreakdown.map(d => (
                                    <div key={d.id} className="p-3.5 rounded-xl bg-slate-50/80 border border-slate-100 flex items-center justify-between">
                                        <div>
                                            <p className="text-xs font-bold text-slate-800">{d.name}</p>
                                            <p className="text-[11px] text-slate-500 font-medium">{d.status}</p>
                                        </div>
                                        <div className="text-right">
                                            <span className="text-sm font-black text-slate-900 font-outfit">{d.percentage}%</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Recommended Specialist Guidance */}
                    {submissionResult.recommendedSpecialists && submissionResult.recommendedSpecialists.length > 0 && (
                        <div className="pt-4 text-left p-5 rounded-2xl bg-indigo-50/60 border border-indigo-100 space-y-2">
                            <div className="flex items-center gap-2 text-indigo-700">
                                <Sparkles className="h-4 w-4" />
                                <h4 className="text-xs font-black uppercase tracking-wider">Suggested Care Specialist</h4>
                            </div>
                            <p className="text-xs text-indigo-900 font-semibold">
                                {submissionResult.recommendedSpecialists[0]?.specialty}
                            </p>
                            <p className="text-xs text-indigo-700/80 font-medium">
                                {submissionResult.recommendedSpecialists[0]?.reason}
                            </p>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="pt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
                        <Button
                            onClick={() => navigate('/Screening_test/patient/analysis')}
                            className="w-full sm:w-auto h-12 px-6 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs uppercase tracking-wider shadow-md transition-all flex items-center justify-center gap-2"
                        >
                            <FileText className="h-4 w-4" /> View Detailed Analysis
                        </Button>

                        <a
                            href="/doctors"
                            className="w-full sm:w-auto h-12 px-6 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs uppercase tracking-wider shadow-md transition-all flex items-center justify-center gap-2"
                        >
                            <Calendar className="h-4 w-4" /> Book Doctor Consultation
                        </a>

                        <Button
                            variant="ghost"
                            onClick={() => navigate('/Screening_test/patient')}
                            className="w-full sm:w-auto h-12 px-5 rounded-xl text-slate-600 hover:text-slate-900 text-xs font-bold uppercase tracking-wider"
                        >
                            Back to Dashboard
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    // ==========================================
    // STEP 3: PRE-SUBMISSION REVIEW VIEW
    // ==========================================
    if (currentStep === REVIEW_STEP) {
        const previewEvaluation = isScreening ? evaluateScreeningAnswers(answers, remarks) : null;
        const allQuestionsCount = isScreening 
            ? SCREENING_SECTIONS.flatMap(s => s.questions).filter(q => q.options).length
            : questions.length;
        const answeredCount = Object.keys(answers).filter(k => answers[k] !== undefined && answers[k] !== '').length;

        return (
            <div className="max-w-4xl mx-auto space-y-8 py-10 font-inter px-4 animate-in fade-in duration-300">
                {/* Stepper Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white/90 backdrop-blur-md p-6 rounded-3xl border border-slate-200/80 shadow-soft">
                    <div className="flex items-center gap-4">
                        <div className="h-12 w-12 rounded-2xl bg-indigo-600 flex items-center justify-center text-white shadow-md">
                            <CheckCircle2 className="h-6 w-6" />
                        </div>
                        <div>
                            <span className="text-[10px] font-black uppercase tracking-widest text-indigo-600">Step 3 of 3 • Review</span>
                            <h1 className="text-xl sm:text-2xl font-black text-slate-900 font-outfit tracking-tight">
                                Review & Confirm Screening
                            </h1>
                        </div>
                    </div>
                    <Button 
                        variant="ghost" 
                        onClick={() => setCurrentStep(DOMAINS.length - 1)}
                        className="text-xs font-bold text-slate-500 hover:text-indigo-600"
                    >
                        <ArrowLeft className="mr-1.5 h-3.5 w-3.5" /> Back to Questions
                    </Button>
                </div>

                {/* Pre-submission Summary Card */}
                <div className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200/80 shadow-soft space-y-6">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-slate-100">
                        <div>
                            <h3 className="text-lg font-black text-slate-900 font-outfit">
                                Readiness Check
                            </h3>
                            <p className="text-xs text-slate-500 font-medium mt-0.5">
                                Please review your answers before final clinical evaluation submission.
                            </p>
                        </div>
                        <div className="flex items-center gap-3">
                            <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 font-bold px-3 py-1">
                                {answeredCount} Answers Logged
                            </Badge>
                        </div>
                    </div>

                    {/* Section Breakdown Grid */}
                    {isScreening && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {SCREENING_SECTIONS.map((sec, idx) => {
                                const secAnswered = sec.questions.filter(q => answers[q.id] !== undefined && answers[q.id] !== '').length;
                                return (
                                    <div key={sec.id} className="p-5 rounded-2xl bg-slate-50 border border-slate-200/60 flex items-center justify-between">
                                        <div className="space-y-1">
                                            <p className="text-[11px] font-black text-slate-400 uppercase tracking-wider">Part {idx + 1}</p>
                                            <p className="text-sm font-bold text-slate-800">{sec.title}</p>
                                            <p className="text-xs text-slate-500">{secAnswered} of {sec.questions.length} completed</p>
                                        </div>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setCurrentStep(idx)}
                                            className="rounded-xl text-xs font-bold hover:bg-white"
                                        >
                                            Edit
                                        </Button>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Red Flags / Priority Concerns Preview */}
                    {previewEvaluation?.redFlags?.length > 0 && (
                        <div className="p-5 rounded-2xl bg-amber-50/80 border border-amber-200/80 space-y-3">
                            <div className="flex items-center gap-2 text-amber-800 font-bold text-xs uppercase tracking-wider">
                                <ShieldAlert className="h-4 w-4" />
                                <span>Priority Milestone Concerns Noted ({previewEvaluation.redFlags.length})</span>
                            </div>
                            <ul className="space-y-2">
                                {previewEvaluation.redFlags.map(rf => (
                                    <li key={rf.id} className="text-xs text-amber-900 font-medium flex items-start gap-2">
                                        <span className="h-1.5 w-1.5 rounded-full bg-amber-500 mt-1.5 shrink-0" />
                                        <span><b>{rf.title}:</b> {rf.recommendation}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Additional Caregiver Context / Remarks */}
                    <div className="space-y-2 pt-2">
                        <label className="block text-xs font-black uppercase tracking-wider text-slate-600">
                            Additional Parent / Caregiver Context (Optional)
                        </label>
                        <textarea
                            value={additionalNotes}
                            onChange={(e) => setAdditionalNotes(e.target.value)}
                            rows={3}
                            placeholder="Add any specific questions you would like the doctor or therapist to address during consultation..."
                            className="w-full p-4 rounded-2xl border-2 border-slate-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-50 outline-none text-sm font-medium text-slate-700 bg-slate-50/50 focus:bg-white transition-all placeholder:text-slate-300"
                        />
                    </div>

                    {/* Confirm CTA */}
                    <div className="pt-6 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4">
                        <Button
                            variant="ghost"
                            onClick={() => setCurrentStep(DOMAINS.length - 1)}
                            className="w-full sm:w-auto h-12 px-6 rounded-xl text-slate-500 font-bold text-xs uppercase tracking-wider"
                        >
                            <ChevronLeft className="mr-1 h-4 w-4" /> Back to Review Questions
                        </Button>

                        <Button
                            onClick={handleFinalSubmit}
                            className="w-full sm:w-auto h-14 px-10 rounded-2xl bg-indigo-600 hover:bg-indigo-700 text-white font-black text-xs uppercase tracking-[0.15em] shadow-premium transition-all flex items-center justify-center gap-2"
                        >
                            Confirm & Submit Screening <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    // ==========================================
    // STEPS 1 & 2: QUESTIONNAIRE FLOW
    // ==========================================
    return (
        <div className="max-w-4xl mx-auto space-y-8 py-10 font-inter px-4 animate-in fade-in duration-300">
            {/* Wizard Header Bar */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white/90 backdrop-blur-md p-6 rounded-3xl border border-slate-200/80 shadow-soft sticky top-4 z-40">
                <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-2xl bg-indigo-600 flex items-center justify-center text-white shadow-md">
                        <Stethoscope className="h-6 w-6" />
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <span className="text-[10px] font-black uppercase tracking-widest text-indigo-600">
                                Step {currentStep + 1} of {DOMAINS.length + 1}
                            </span>
                            <span className="text-slate-300">•</span>
                            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                {isScreening ? `Part ${currentStep + 1}: ${DOMAINS[currentStep]?.title}` : 'Questionnaire'}
                            </span>
                        </div>
                        <h1 className="text-xl sm:text-2xl font-black text-slate-900 font-outfit tracking-tight">
                            {isScreening ? 'Developmental Screening' : matchedForm?.title}
                        </h1>
                    </div>
                </div>

                <div className="flex items-center gap-4">
                    <div className="text-right">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Progress</p>
                        <p className="text-base font-black text-indigo-600 font-outfit">{progress}%</p>
                    </div>
                    <div className="w-24 sm:w-32 h-2.5 bg-slate-100 rounded-full overflow-hidden border border-slate-200">
                        <div className="h-full bg-indigo-600 transition-all duration-300 ease-out" style={{ width: `${progress}%` }} />
                    </div>
                </div>
            </div>

            {/* Stepper Navigation Indicator */}
            {isScreening && (
                <div className="grid grid-cols-3 gap-2 sm:gap-4 px-1">
                    {SCREENING_SECTIONS.map((sec, idx) => {
                        const isCurrent = currentStep === idx;
                        const isDone = currentStep > idx;
                        return (
                            <button
                                key={sec.id}
                                type="button"
                                onClick={() => setCurrentStep(idx)}
                                className={`p-3 sm:p-4 rounded-2xl text-left border transition-all ${
                                    isCurrent 
                                        ? 'bg-indigo-50/80 border-indigo-200 shadow-sm ring-2 ring-indigo-100'
                                        : isDone
                                        ? 'bg-white border-emerald-200 hover:border-emerald-300'
                                        : 'bg-white/60 border-slate-100 opacity-60'
                                }`}
                            >
                                <div className="flex items-center gap-2">
                                    <span className={`h-5 w-5 rounded-full flex items-center justify-center text-[10px] font-black shrink-0 ${
                                        isDone ? 'bg-emerald-500 text-white' : isCurrent ? 'bg-indigo-600 text-white' : 'bg-slate-200 text-slate-600'
                                    }`}>
                                        {isDone ? <Check className="h-3 w-3 stroke-[3]" /> : idx + 1}
                                    </span>
                                    <span className={`text-xs font-bold truncate ${isCurrent ? 'text-indigo-900' : 'text-slate-700'}`}>
                                        Part {idx + 1}
                                    </span>
                                </div>
                            </button>
                        );
                    })}

                    {/* Review Step Tab */}
                    <button
                        type="button"
                        onClick={() => {
                            if (isStepComplete()) setCurrentStep(REVIEW_STEP);
                        }}
                        className={`p-3 sm:p-4 rounded-2xl text-left border transition-all ${
                            currentStep === REVIEW_STEP 
                                ? 'bg-indigo-50/80 border-indigo-200 shadow-sm ring-2 ring-indigo-100'
                                : 'bg-white/60 border-slate-100 opacity-60'
                        }`}
                    >
                        <div className="flex items-center gap-2">
                            <span className="h-5 w-5 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center text-[10px] font-black shrink-0">
                                3
                            </span>
                            <span className="text-xs font-bold text-slate-700 truncate">
                                Review & Submit
                            </span>
                        </div>
                    </button>
                </div>
            )}

            {/* Restored Toast */}
            {restored && (
                <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2.5 px-6 py-3 rounded-2xl shadow-xl bg-slate-900 text-white text-xs font-bold tracking-wide animate-in fade-in slide-in-from-bottom-2">
                    <Check className="h-4 w-4 text-emerald-400" /> Draft restored from your last session
                </div>
            )}

            {/* Main Content Card */}
            <div>
                {isScreening ? (
                    <ScreeningAssessment
                        section={SCREENING_SECTIONS[currentStep]}
                        answers={answers}
                        onChange={handleAnswer}
                    />
                ) : (
                    <Card className="border-0 shadow-premium rounded-[2.5rem] bg-white border border-slate-100 overflow-hidden">
                        <CardHeader className="p-8 pb-0 border-b border-slate-50 flex flex-row items-center justify-between gap-4">
                            <div className="flex items-center gap-3">
                                <Badge className="bg-indigo-50 text-indigo-700 border-indigo-100 px-3.5 py-1 text-[10px] font-black uppercase tracking-widest rounded-xl">
                                    Item {currentStep + 1} of {questions.length}
                                </Badge>
                                {isRequired ? (
                                    <Badge className="bg-red-50 text-red-600 border-red-100 px-2.5 py-0.5 text-[9px] font-bold rounded-xl">
                                        Required *
                                    </Badge>
                                ) : (
                                    <Badge className="bg-slate-50 text-slate-400 border-slate-100 px-2.5 py-0.5 text-[9px] font-medium rounded-xl">
                                        Optional
                                    </Badge>
                                )}
                            </div>
                        </CardHeader>

                        <CardContent className="p-8 sm:p-12 space-y-8">
                            <div className="max-w-2xl mx-auto w-full space-y-6">
                                <div className="text-center space-y-2">
                                    <h2 className="text-xl sm:text-3xl font-black text-slate-900 font-outfit tracking-tight">
                                        {questionTitle}
                                    </h2>
                                    {questionDescription && (
                                        <p className="text-sm text-slate-500 font-medium">
                                            {questionDescription}
                                        </p>
                                    )}
                                </div>

                                {/* Multiple Choice */}
                                {questionType === 'multiple_choice' && (
                                    <div className="space-y-2.5 pt-2">
                                        {(currentQ.options || []).map((opt, oIdx) => (
                                            <button
                                                key={oIdx}
                                                type="button"
                                                onClick={() => handleSelect(opt)}
                                                className={`w-full p-4 sm:p-5 rounded-2xl border-2 text-left transition-all flex items-center justify-between ${
                                                    currentAnswer === opt
                                                        ? 'border-indigo-600 bg-indigo-50/70 text-indigo-900 font-bold shadow-sm'
                                                        : 'border-slate-100 bg-white hover:border-slate-200 text-slate-700 font-medium'
                                                }`}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className={`h-5 w-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                                                        currentAnswer === opt ? 'border-indigo-600 bg-indigo-600' : 'border-slate-300'
                                                    }`}>
                                                        {currentAnswer === opt && <div className="h-2 w-2 rounded-full bg-white" />}
                                                    </div>
                                                    <span className="text-sm">{opt}</span>
                                                </div>
                                                {currentAnswer === opt && <Check className="h-5 w-5 text-indigo-600 shrink-0" />}
                                            </button>
                                        ))}
                                    </div>
                                )}

                                {/* Boolean */}
                                {questionType === 'boolean' && (
                                    <div className="grid grid-cols-2 gap-4 pt-2">
                                        {['Yes', 'No'].map(val => (
                                            <button
                                                key={val}
                                                type="button"
                                                onClick={() => handleSelect(val)}
                                                className={`h-16 rounded-2xl font-black text-lg transition-all border-2 flex items-center justify-center gap-2 ${
                                                    currentAnswer === val
                                                        ? val === 'Yes'
                                                            ? 'bg-emerald-600 border-emerald-600 text-white shadow-md'
                                                            : 'bg-red-600 border-red-600 text-white shadow-md'
                                                        : 'bg-white border-slate-200 text-slate-700 hover:border-indigo-300'
                                                }`}
                                            >
                                                {val} {currentAnswer === val && <Check className="h-5 w-5" />}
                                            </button>
                                        ))}
                                    </div>
                                )}

                                {/* Rating */}
                                {questionType === 'rating' && (
                                    <div className="space-y-4 pt-2">
                                        <div className="flex items-center justify-between text-xs font-bold text-slate-400 uppercase tracking-wider px-2">
                                            <span>{currentQ.scaleMinLabel || 'Minimum'}</span>
                                            <span>{currentQ.scaleMaxLabel || 'Maximum'}</span>
                                        </div>
                                        <div className="flex items-center justify-center gap-3 flex-wrap">
                                            {Array.from({ length: currentQ.scaleMax || 5 }, (_, i) => i + 1).map(num => (
                                                <button
                                                    key={num}
                                                    type="button"
                                                    onClick={() => handleSelect(num)}
                                                    className={`h-14 w-14 rounded-2xl font-black text-lg transition-all border-2 flex items-center justify-center ${
                                                        currentAnswer === num
                                                            ? 'bg-indigo-600 border-indigo-600 text-white shadow-md scale-105'
                                                            : 'bg-white border-slate-200 text-slate-700 hover:border-indigo-300 hover:bg-indigo-50'
                                                    }`}
                                                >
                                                    {num}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Legacy Rating */}
                                {questionType === 'legacy_rating' && (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                                        {[
                                            { val: 4, label: "Always / Independent" },
                                            { val: 3, label: "Often / Minimal Prompts" },
                                            { val: 2, label: "Sometimes / Moderate Help" },
                                            { val: 1, label: "Rarely / Fully Dependent" }
                                        ].map(option => (
                                            <button
                                                key={option.val}
                                                type="button"
                                                onClick={() => handleSelect(option.val)}
                                                className={`p-5 border-2 rounded-2xl text-left transition-all flex items-center justify-between ${
                                                    currentAnswer === option.val
                                                        ? 'border-indigo-600 bg-indigo-50 text-indigo-900 font-bold'
                                                        : 'text-slate-700 bg-white border-slate-100 hover:border-slate-200'
                                                }`}
                                            >
                                                <span className="text-sm font-semibold">{option.label}</span>
                                                {currentAnswer === option.val && <Check className="h-5 w-5 text-indigo-600" />}
                                            </button>
                                        ))}
                                    </div>
                                )}

                                {/* Observational Remarks */}
                                <div className="text-left space-y-2 pt-4 border-t border-slate-100">
                                    <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                                        Observational Notes (Optional)
                                    </label>
                                    <textarea
                                        className="w-full min-h-[90px] rounded-2xl border-2 border-slate-100 bg-slate-50 px-4 py-3 text-sm focus:bg-white focus:border-indigo-500 outline-none transition-all placeholder:text-slate-300 font-medium"
                                        placeholder="Add any specific observations or context..."
                                        value={remarks[currentStep] || ''}
                                        onChange={handleRemarkChange}
                                    />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>

            {/* Navigation Footer */}
            <div className="flex justify-between items-center bg-white/90 backdrop-blur-md p-5 sm:p-6 rounded-3xl border border-slate-200/80 shadow-soft">
                <Button 
                    variant="ghost" 
                    onClick={() => navigate('/Screening_test/patient')} 
                    className="h-12 rounded-xl px-5 text-xs font-bold uppercase tracking-wider text-slate-400 hover:text-red-500 transition-colors"
                >
                    Cancel <X className="ml-1.5 h-3.5 w-3.5" />
                </Button>

                <div className="flex items-center gap-3">
                    {currentStep > 0 && (
                        <Button 
                            variant="outline" 
                            onClick={handlePrev} 
                            className="h-12 w-12 rounded-xl border-slate-200 bg-white hover:bg-slate-50"
                        >
                            <ChevronLeft className="h-5 w-5 text-slate-600" />
                        </Button>
                    )}
                    <Button
                        onClick={handleNext}
                        disabled={!isStepComplete()}
                        className="h-12 rounded-xl px-8 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs uppercase tracking-wider shadow-md transition-all disabled:opacity-50 flex items-center gap-2"
                    >
                        {currentStep === DOMAINS.length - 1 ? "Review & Confirm" : "Next Section"} 
                        <ChevronRight className="h-4 w-4" />
                    </Button>
                </div>
            </div>
        </div>
    );
}
